import React, { useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { STATUS } from "../../../constant";
import { toast } from "react-toastify";
import {
  viewApiReset,
  viewApiRequest,
  updateFspCodeRequest,
  updateFspCodeReset,
  updateOutboundCredentialsRequest,
  updateOutboundCredentialsReset,
  rejectApiRequest,
  rejectApiReset,
  approveApiRequest,
  approveApiReset,
  deactivateApiRequest,
  activateApiRequest,
  deactivateApiReset,
  activateApiReset,
} from "../../../store/actions";
import {
  Box,
  ColorPaletteProp,
  Sheet,
  Table,
  Button,
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  FormControl,
  FormLabel,
  Input,
  Stack,
  DialogTitle,
  DialogContent,
} from "@mui/joy";
import { LoadingView, PageTitle, StatusBadge } from "../../../components";
import { formatDate } from "../../../utils";
import { NAVIGATE_TO_PARTNERPROPERTIESPAGE } from "../../../route/types";

function ViewApiDetails({
  viewApiStatus,
  viewApiResult,
  viewApiErrorMessage,
  updateFspCodeStatus,
  updateFspCodeResult,
  updateFspCodeErrorMessage,
  updateOutboundCredentialsStatus,
  updateOutboundCredentialsResult,
  updateOutboundCredentialsErrorMessage,
  rejectApiStatus,
  rejectApiResult,
  rejectApiErrorMessage,
  approveApiStatus,
  approveApiResult,
  approveApiErrorMessage,
  activateApiStatus,
  activateApiResult,
  activateApiErrorMessage,
  deactivateApiStatus,
  deactivateApiResult,
  deactivateApiErrorMessage,
  loginResult,
}) {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const id = location.state?.id;

  const [modalOpen, setModalOpen] = useState(false);
  const [fspCode, setFspCode] = useState("");
  const [rejectModal, setRejectModal] = useState(false);
  const [deactivateModal, setDeactivateModal] = useState(false);
  const [api_id, setApiID] = useState("");
  const [remark, setRemark] = useState("");

  const [outboundModal, setOutboundModal] = useState(false);
  const [outboundClientId, setOutboundClientId] = useState("");
  const [outboundSecretKey, setOutboundSecretKey] = useState("");

  const hasOutboundCredentials = Boolean(
    viewApiResult?.outbound_client_id?.trim() &&
      viewApiResult?.outbound_secret_key?.trim(),
  );

  // ✅ Fixed: pass details correctly
  const approveApi = (details) => {
    dispatch(approveApiRequest(loginResult.token, { id: details.id }));
  };

  // ✅ Fixed: added e.preventDefault()
  const rejectApi = (e) => {
    e.preventDefault();
    dispatch(rejectApiRequest(loginResult.token, { id: api_id, remark }));
  };

  // ✅ Fixed: pass details correctly
  const openReject = (details) => {
    setRemark("");
    setApiID(details.id);
    setRejectModal(true);
  };

  // ✅ Fixed: pass details correctly
  const activateApi = (details) => {
    dispatch(activateApiRequest(loginResult.token, { id: details.id }));
  };

  // ✅ Fixed: added e.preventDefault()
  const deactivateApi = (e) => {
    e.preventDefault();
    dispatch(deactivateApiRequest(loginResult.token, { id: api_id, remark }));
  };

  // ✅ Fixed: pass details correctly
  const openDeactivateModal = (details) => {
    setRemark("");
    setApiID(details.id);
    setDeactivateModal(true);
  };

  const checkLoading = () =>
    [
      viewApiStatus,
      approveApiStatus,
      rejectApiStatus,
      activateApiStatus,
      deactivateApiStatus,
      updateOutboundCredentialsStatus,
    ].includes(STATUS.LOADING);

  const goToPartnerPropertiesPage = (result) => {
    navigate(NAVIGATE_TO_PARTNERPROPERTIESPAGE, {
      state: { fsp_code: result?.fsp_code },
    });
  };

  const openModal = () => {
    setFspCode(viewApiResult?.fsp_code ?? "");
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setFspCode("");
  };

  const handleUpdateFspCode = () => {
    if (!fspCode.trim()) {
      toast.error("FSP Code is required");
      return;
    }
    dispatch(
      updateFspCodeRequest(loginResult?.token, {
        id,
        fsp_code: fspCode.trim(),
      }),
    );
  };

  const openOutboundModal = () => {
    setOutboundClientId(viewApiResult?.outbound_client_id ?? "");
    setOutboundSecretKey(viewApiResult?.outbound_secret_key ?? "");
    setOutboundModal(true);
  };

  const closeOutboundModal = () => {
    setOutboundModal(false);
    setOutboundClientId("");
    setOutboundSecretKey("");
  };

  const handleUpdateOutboundCredentials = (e) => {
    e.preventDefault();

    if (!outboundClientId.trim() || !outboundSecretKey.trim()) {
      toast.error("Both outbound client ID and secret key are required");
      return;
    }

    dispatch(
      updateOutboundCredentialsRequest(loginResult?.token, {
        id,
        outbound_client_id: outboundClientId.trim(),
        outbound_secret_key: outboundSecretKey.trim(),
      }),
    );
  };

  useEffect(() => {
    dispatch(viewApiRequest(loginResult?.token, { id }));
  }, [dispatch, id, loginResult]);

  useEffect(() => {
    if (viewApiStatus === STATUS.ERROR) {
      toast.error(viewApiErrorMessage, { toastId: "viewApiError" });
      dispatch(viewApiReset());
    }
  }, [dispatch, viewApiErrorMessage, viewApiStatus]);

  useEffect(() => {
    if (updateFspCodeStatus === STATUS.SUCCESS) {
      toast.success("FSP Code updated successfully");
      closeModal();
      dispatch(updateFspCodeReset());
      dispatch(viewApiRequest(loginResult?.token, { id }));
    }
    if (updateFspCodeStatus === STATUS.ERROR) {
      toast.error(updateFspCodeErrorMessage, { toastId: "updateFspCodeError" });
      dispatch(updateFspCodeReset());
    }
  }, [
    updateFspCodeStatus,
    updateFspCodeErrorMessage,
    dispatch,
    loginResult,
    id,
  ]);

  useEffect(() => {
    if (updateOutboundCredentialsStatus === STATUS.SUCCESS) {
      toast.success("Outbound credentials updated successfully");
      closeOutboundModal();
      dispatch(updateOutboundCredentialsReset());
      dispatch(viewApiRequest(loginResult?.token, { id }));
    }

    if (updateOutboundCredentialsStatus === STATUS.ERROR) {
      toast.error(updateOutboundCredentialsErrorMessage, {
        toastId: "updateOutboundCredentialsError",
      });
      dispatch(updateOutboundCredentialsReset());
    }
  }, [
    updateOutboundCredentialsStatus,
    updateOutboundCredentialsErrorMessage,
    dispatch,
    loginResult,
    id,
  ]);

  useEffect(() => {
    if (approveApiStatus === STATUS.SUCCESS) {
      toast.success("API approved successfully");
      dispatch(approveApiReset());
      dispatch(viewApiRequest(loginResult?.token, { id }));
    } else if (approveApiStatus === STATUS.ERROR) {
      toast.error(approveApiErrorMessage);
      dispatch(approveApiReset());
    }
  }, [approveApiStatus, approveApiErrorMessage, id, dispatch, loginResult]);

  useEffect(() => {
    if (rejectApiStatus === STATUS.SUCCESS) {
      toast.success("API rejected successfully");
      setRejectModal(false);
      dispatch(rejectApiReset());
      dispatch(viewApiRequest(loginResult?.token, { id }));
    } else if (rejectApiStatus === STATUS.ERROR) {
      toast.error(rejectApiErrorMessage);
      dispatch(rejectApiReset());
    }
  }, [rejectApiStatus, rejectApiErrorMessage, id, dispatch, loginResult]);

  useEffect(() => {
    if (activateApiStatus === STATUS.SUCCESS) {
      toast.success("API activated successfully");
      dispatch(activateApiReset());
      dispatch(viewApiRequest(loginResult?.token, { id }));
    } else if (activateApiStatus === STATUS.ERROR) {
      toast.error(activateApiErrorMessage);
      dispatch(activateApiReset());
    }
  }, [activateApiStatus, activateApiErrorMessage, id, dispatch, loginResult]);

  useEffect(() => {
    if (deactivateApiStatus === STATUS.SUCCESS) {
      toast.success("API deactivated successfully");
      setDeactivateModal(false);
      dispatch(deactivateApiReset());
      dispatch(viewApiRequest(loginResult?.token, { id }));
    } else if (deactivateApiStatus === STATUS.ERROR) {
      toast.error(deactivateApiErrorMessage);
      dispatch(deactivateApiReset());
    }
  }, [
    deactivateApiStatus,
    deactivateApiErrorMessage,
    id,
    dispatch,
    loginResult,
  ]);

  return (
    <Box>
      <PageTitle title="API Access View" />
      <LoadingView loading={checkLoading()} />

      {/* Action Buttons */}
      <Box
        sx={{
          mb: 3,
          mx: 2,
          display: "flex",
          gap: 2,
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        <Button
          color="primary"
          onClick={() => goToPartnerPropertiesPage(viewApiResult)}
        >
          View Partner Properties
        </Button>

        <Button color="warning" onClick={openModal}>
          Update FSP Code
        </Button>

        <Button color="primary" onClick={openOutboundModal}>
          {hasOutboundCredentials
            ? "Edit Outbound Credentials"
            : "Add Outbound Credentials"}
        </Button>

        {/* ✅ Fixed: all onClick handlers now actually call the functions with viewApiResult */}
        {viewApiResult?.status !== "5" &&
          viewApiResult?.status !== "4" &&
          (viewApiResult?.status === "3" ? (
            <>
              <Button color="success" onClick={() => approveApi(viewApiResult)}>
                Approve
              </Button>
              <Button color="danger" onClick={() => openReject(viewApiResult)}>
                Reject
              </Button>
            </>
          ) : viewApiResult?.status === "1" ? (
            <Button
              color="danger"
              onClick={() => openDeactivateModal(viewApiResult)}
            >
              Deactivate
            </Button>
          ) : (
            <Button color="success" onClick={() => activateApi(viewApiResult)}>
              Activate
            </Button>
          ))}
      </Box>

      {/* Details Table */}
      <Sheet
        variant="outlined"
        sx={{ mx: 2, mb: 2, p: 2, borderRadius: "sm", overflow: "auto" }}
      >
        <Table size="sm" sx={{ "& td": { py: 1.5 } }}>
          <tbody>
            {[
              ["Client ID", viewApiResult?.client_id],
              ["Secret Key", viewApiResult?.secret_key],
              ["Outbound Client ID", viewApiResult?.outbound_client_id],
              ["Outbound Secret Key", viewApiResult?.outbound_secret_key],
              ["Owner Name", viewApiResult?.owner_name],
              ["Email", viewApiResult?.email],
              ["AES Key", viewApiResult?.aes_key],
              ["FSP Code", viewApiResult?.fsp_code],
              ["Access Token", viewApiResult?.access_token],
              [
                "Access Token Expire",
                formatDate(viewApiResult?.access_token_expire),
              ],
              ["Created At", formatDate(viewApiResult?.created_at)],
              ["Created By", viewApiResult?.created_by],
              ["Approved At", formatDate(viewApiResult?.approved_at)],
              ["Approved By", viewApiResult?.approved_by],
            ].map(([label, value]) => (
              <tr key={label}>
                <td
                  style={{
                    width: "200px",
                    fontWeight: "bold",
                    verticalAlign: "top",
                  }}
                >
                  {label}:
                </td>
                <td style={{ wordBreak: "break-all" }}>{value}</td>
              </tr>
            ))}
            <tr>
              <td style={{ fontWeight: "bold", verticalAlign: "top" }}>
                Status:
              </td>
              <td>
                <StatusBadge
                  color={
                    ({
                      1: "success",
                      2: "warning",
                      3: "neutral",
                      4: "danger",
                      5: "neutral",
                    }[viewApiResult?.status] ?? "neutral") as ColorPaletteProp
                  }
                >
                  {
                    {
                      1: "Active",
                      2: "Inactive",
                      3: "Pending Approval",
                      4: "Rejected",
                      5: "Pending Additional",
                    }[viewApiResult?.status]
                  }
                </StatusBadge>
              </td>
            </tr>
          </tbody>
        </Table>
      </Sheet>

      {/* Update FSP Code Modal */}
      <Modal open={modalOpen} onClose={closeModal}>
        <ModalDialog sx={{ minWidth: 400 }}>
          <ModalClose />
          <Typography level="h4">Update FSP Code</Typography>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl>
              <FormLabel>FSP Code</FormLabel>
              <Input
                placeholder="Enter FSP Code"
                value={fspCode}
                onChange={(e) => setFspCode(e.target.value)}
              />
            </FormControl>
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button variant="outlined" color="neutral" onClick={closeModal}>
                Cancel
              </Button>
              <Button
                color="warning"
                loading={updateFspCodeStatus === STATUS.LOADING}
                onClick={handleUpdateFspCode}
              >
                Update
              </Button>
            </Box>
          </Stack>
        </ModalDialog>
      </Modal>

      {/* Outbound Credentials Modal */}
      <Modal open={outboundModal} onClose={closeOutboundModal}>
        <ModalDialog
          aria-labelledby="outbound-credentials-title"
          sx={{ minWidth: 400 }}
        >
          <ModalClose />
          <Typography level="h4" id="outbound-credentials-title">
            {hasOutboundCredentials
              ? "Edit Outbound Credentials"
              : "Add Outbound Credentials"}
          </Typography>
          <Stack
            component="form"
            onSubmit={handleUpdateOutboundCredentials}
            spacing={2}
            sx={{ mt: 1 }}
          >
            <FormControl required>
              <FormLabel>Outbound Client ID</FormLabel>
              <Input
                type="text"
                placeholder="Enter outbound client ID"
                value={outboundClientId}
                onChange={(e) => setOutboundClientId(e.target.value)}
              />
            </FormControl>
            <FormControl required>
              <FormLabel>Outbound Secret Key</FormLabel>
              <Input
                type="text"
                placeholder="Enter outbound secret key"
                value={outboundSecretKey}
                onChange={(e) => setOutboundSecretKey(e.target.value)}
              />
            </FormControl>
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button
                type="button"
                variant="outlined"
                color="neutral"
                onClick={closeOutboundModal}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                loading={updateOutboundCredentialsStatus === STATUS.LOADING}
              >
                {hasOutboundCredentials ? "SAVE" : "ADD"}
              </Button>
            </Box>
          </Stack>
        </ModalDialog>
      </Modal>

      {/* Reject Modal */}
      <Modal open={rejectModal}>
        <ModalDialog
          sx={(theme) => ({
            [theme.breakpoints.only("xs")]: {
              bottom: 0,
              left: 0,
              right: 0,
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              transform: "none",
              maxWidth: "unset",
              overflow: "auto",
            },
          })}
        >
          <ModalClose
            variant="outlined"
            onClick={() => setRejectModal(false)}
          />
          <DialogTitle>Reject API: {api_id}</DialogTitle>
          <DialogContent>Please write a reason for rejection.</DialogContent>
          {/* ✅ Fixed: e.preventDefault() inside rejectApi */}
          <Stack component="form" onSubmit={rejectApi} gap={2} sx={{ mt: 2 }}>
            <FormControl required>
              <FormLabel>Rejection reason</FormLabel>
              <Input
                type="text"
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                placeholder="Enter rejection reason"
              />
            </FormControl>
            <Button
              type="submit"
              fullWidth
              color="danger"
              loading={rejectApiStatus === STATUS.LOADING}
            >
              REJECT
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>

      {/* Deactivate Modal */}
      <Modal open={deactivateModal}>
        <ModalDialog
          sx={(theme) => ({
            [theme.breakpoints.only("xs")]: {
              bottom: 0,
              left: 0,
              right: 0,
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              transform: "none",
              maxWidth: "unset",
              overflow: "auto",
            },
          })}
        >
          <ModalClose
            variant="outlined"
            onClick={() => setDeactivateModal(false)}
          />
          <DialogTitle>Deactivate API: {api_id}</DialogTitle>
          <DialogContent>Please write a reason for deactivation.</DialogContent>
          {/* ✅ Fixed: e.preventDefault() inside deactivateApi */}
          <Stack
            component="form"
            onSubmit={deactivateApi}
            gap={2}
            sx={{ mt: 2 }}
          >
            <FormControl required>
              <FormLabel>Deactivation reason</FormLabel>
              <Input
                type="text"
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                placeholder="Enter deactivation reason"
              />
            </FormControl>
            <Button
              type="submit"
              fullWidth
              color="danger"
              loading={deactivateApiStatus === STATUS.LOADING}
            >
              DEACTIVATE
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>
    </Box>
  );
}

const mapStateToProps = ({ api, auth }) => {
  const { otpResult: loginResult } = auth;
  const {
    viewApiStatus,
    viewApiResult,
    viewApiErrorMessage,
    updateFspCodeStatus,
    updateFspCodeResult,
    updateFspCodeErrorMessage,
    updateOutboundCredentialsStatus,
    updateOutboundCredentialsResult,
    updateOutboundCredentialsErrorMessage,
    rejectApiStatus,
    rejectApiResult,
    rejectApiErrorMessage,
    approveApiStatus,
    approveApiResult,
    approveApiErrorMessage,
    activateApiStatus,
    activateApiResult,
    activateApiErrorMessage,
    deactivateApiStatus,
    deactivateApiResult,
    deactivateApiErrorMessage,
  } = api;

  return {
    loginResult,
    viewApiStatus,
    viewApiResult,
    viewApiErrorMessage,
    updateFspCodeStatus,
    updateFspCodeResult,
    updateFspCodeErrorMessage,
    updateOutboundCredentialsStatus,
    updateOutboundCredentialsResult,
    updateOutboundCredentialsErrorMessage,
    rejectApiStatus,
    rejectApiResult,
    rejectApiErrorMessage,
    approveApiStatus,
    approveApiResult,
    approveApiErrorMessage,
    activateApiStatus,
    activateApiResult,
    activateApiErrorMessage,
    deactivateApiStatus,
    deactivateApiResult,
    deactivateApiErrorMessage,
  };
};

export default connect(mapStateToProps, {})(ViewApiDetails);
